import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/mogu-data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
            <h1 className="mt-4 text-xl font-heading font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该文章可能已被删除或不存在</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>返回资讯中心
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '资讯中心', path: '/articles' },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4">
              <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 font-medium">
                {article.category}
              </span>
              <span>{article.date}</span>
              <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
          </div>

          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose prose-sm max-w-none text-foreground-800 leading-relaxed space-y-4">
            <p>{article.content}</p>
            <p>蘑菇tv一直致力于为用户提供最优质的直播和视频观看体验。通过持续的技术创新和内容生态建设，我们已经成长为国内领先的直播平台之一。无论你是游戏爱好者、音乐发烧友，还是美食达人、旅行探险家，蘑菇tv都有属于你的精彩内容。</p>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mt-8">关于蘑菇tv</h2>
            <p>蘑菇tv成立于2020年，是国内领先的高清直播与短视频平台。平台汇聚了超过10万名优秀主播，覆盖游戏、娱乐、生活、体育、美食、音乐、户外等多个内容领域。我们致力于通过技术创新和优质内容，为用户打造极致的视听体验。</p>
            <p>2026年，蘑菇tv日活跃用户突破5000万，日均直播时长超过80万小时。平台采用自研流媒体传输技术，支持1080P高清画质和超低延迟直播，让每一位用户都能享受流畅清晰的观看体验。</p>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mt-8">技术优势</h2>
            <p>蘑菇tv技术团队自主研发了高效的视频编码算法和智能CDN调度系统，确保在全国各地都能获得稳定流畅的直播体验。我们的AI推荐系统能够精准分析用户偏好，为每位用户推荐最感兴趣的内容和主播。</p>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200">
            <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={ra.cover}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 font-medium">
                      {ra.category}
                    </span>
                    <h4 className="mt-2 text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            image: article.cover,
            author: { '@type': 'Organization', name: '蘑菇tv' },
            publisher: { '@type': 'Organization', name: '蘑菇tv', url: 'https://mogu.tv/' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://mogu.tv/article/${article.id}` },
          }),
        }}
      />
    </>
  );
}